// frontend/src/services/api.js - UPDATED FOR JWT AUTHENTICATION
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

console.log('API URL configured as:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // IMPORTANT: We're using JWT, not session cookies
});

let csrfToken = null;
let isFetchingCsrf = false;
let csrfPromise = null;

// Function to fetch CSRF token from Django (when CSRF_USE_SESSIONS = True)
const fetchCsrfToken = async () => {
  if (isFetchingCsrf) {
    return csrfPromise;
  }

  isFetchingCsrf = true;
  csrfPromise = (async () => {
    try {
      console.log('Fetching CSRF token from session...');
      
      // Since CSRF_USE_SESSIONS = True in Django, we need to get it via endpoint
      const endpoints = ['/auth/csrf/', '/auth/csrf-token/'];
      
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(API_BASE_URL + endpoint, {
            withCredentials: false, // No cookies needed
          });
          
          if (response.data.csrf_token) {
            csrfToken = response.data.csrf_token;
            console.log('CSRF token fetched from', endpoint, csrfToken.substring(0, 20) + '...');
            return csrfToken;
          } else if (response.data.detail) {
            console.log('CSRF endpoint responded:', endpoint, response.data.detail);
            // Django might return CSRF token in headers or body differently
            break;
          }
        } catch (error) {
          console.log('CSRF endpoint failed:', endpoint, error.message);
          // Continue to next endpoint
        }
      }
      
      // Alternative: Get CSRF from Django's CSRF token endpoint
      try {
        // This endpoint should exist when CSRF_USE_SESSIONS = True
        const response = await axios.get(API_BASE_URL + '/auth/csrf-token/', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          withCredentials: false,
        });
        
        if (response.data && response.data.csrfToken) {
          csrfToken = response.data.csrfToken;
          console.log('CSRF token from API endpoint:', csrfToken.substring(0, 20) + '...');
          return csrfToken;
        }
      } catch (error) {
        console.log('Alternative CSRF fetch failed:', error.message);
      }
      
      console.warn('Could not fetch CSRF token from server');
      return null;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return null;
    } finally {
      isFetchingCsrf = false;
    }
  })();

  return csrfPromise;
};

// Initialize CSRF token on app start
fetchCsrfToken().catch(console.error);

// Add token and CSRF token to requests
api.interceptors.request.use(
  async (config) => {
    // Add Authorization token if available (JWT)
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for Django (for POST/PUT/PATCH/DELETE requests)
    // Only needed if Django requires CSRF for API endpoints
    const method = config.method.toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      // Ensure we have a CSRF token
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        console.warn('No CSRF token available for', method.toUpperCase(), 'request');
      }
    }

    // Log request for debugging (only in development)
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, {
        data: config.data,
        headers: config.headers
      });
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Handle token refresh and common errors
api.interceptors.response.use(
  (response) => {
    // Check for new CSRF token in response headers (if Django sends it)
    if (response.headers['x-csrftoken']) {
      csrfToken = response.headers['x-csrftoken'];
      console.log('CSRF token updated from response headers');
    }
    
    // Log successful response for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response Success: ${response.config.method.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;
    const url = originalRequest?.url;
    const method = originalRequest?.method;

    // Log error for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error Details:', {
        status,
        url,
        method,
        error: data,
        requestData: originalRequest?.data,
        headers: originalRequest?.headers
      });
    }

    // Handle 401 Unauthorized (token expired)
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Try to refresh token
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        }, {
          withCredentials: false, // No cookies for JWT
          headers: {
            'Content-Type': 'application/json',
          }
        });

        const { access } = response.data;
        localStorage.setItem('access_token', access);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);

        // Clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');

        // Only redirect if we're not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden (CSRF failure or insufficient permissions)
    if (status === 403) {
      const errorMessage = data?.detail || data?.error || 'Access forbidden';
      
      // Check if it's a CSRF error
      if (errorMessage.toLowerCase().includes('csrf') || 
          data?.code === 'csrf_protect') {
        console.log('CSRF token invalid or missing, fetching new one...');
        
        // Fetch new CSRF token and retry
        await fetchCsrfToken();
        if (csrfToken) {
          originalRequest.headers['X-CSRFToken'] = csrfToken;
          return api(originalRequest);
        }
      }
      
      return Promise.reject(new Error(errorMessage));
    }

    // Handle 404 Not Found
    if (status === 404) {
      return Promise.reject(new Error('Resource not found'));
    }

    // Handle 400 Bad Request with validation errors
    if (status === 400) {
      console.error('400 Bad Request Details:', data);

      // Handle Django REST Framework validation errors
      if (typeof data === 'object') {
        // Check for specific error formats
        if (data.error) {
          return Promise.reject(new Error(data.error));
        }

        if (data.detail) {
          return Promise.reject(new Error(data.detail));
        }

        if (data.message) {
          return Promise.reject(new Error(data.message));
        }

        // Try to extract any error messages
        const errors = Object.values(data).flat().join(', ');
        if (errors) {
          return Promise.reject(new Error(errors || 'Invalid request data'));
        }
      }

      // Handle string error messages
      if (typeof data === 'string') {
        return Promise.reject(new Error(data));
      }

      return Promise.reject(new Error('Invalid request data. Please check your input.'));
    }

    // Handle 500 Internal Server Error
    if (status >= 500) {
      return Promise.reject(new Error('Server error. Please try again later.'));
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error - no response received:', error);
      return Promise.reject(new Error('Network error. Please check your internet connection.'));
    }

    // Default error handling
    const errorMessage = data?.detail || data?.error || data?.message || 'An unexpected error occurred';
    return Promise.reject(new Error(errorMessage));
  }
);

// Add helper methods to the api instance
api.getAuthHeaders = async () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Ensure we have CSRF token for write operations
  if (!csrfToken) {
    await fetchCsrfToken();
  }
  
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }

  return headers;
};

api.isAuthenticated = () => {
  // Check for JWT token, not cookies
  return !!(localStorage.getItem('access_token') || localStorage.getItem('token'));
};

api.clearAuth = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

// Get CSRF token helper
api.getCsrfToken = async () => {
  if (!csrfToken) {
    await fetchCsrfToken();
  }
  return csrfToken;
};

// Debug helper to check current configuration
api.debugConfig = () => {
  return {
    baseURL: API_BASE_URL,
    isAuthenticated: api.isAuthenticated(),
    csrfToken: csrfToken ? csrfToken.substring(0, 20) + '...' : null,
    authToken: localStorage.getItem('access_token') ? 'Present' : 'Missing',
    refreshToken: localStorage.getItem('refresh_token') ? 'Present' : 'Missing',
    userData: localStorage.getItem('user') ? 'Present' : 'Missing',
    cookies: document.cookie, // For debugging only
  };
};

// Test API connection (without relying on cookies)
api.testConnection = async () => {
  try {
    // Test with a public endpoint first
    const response = await api.get('/auth/csrf-token/');
    console.log('API Connection Test:', response.status, response.data);
    return true;
  } catch (error) {
    console.error('API Connection Test Failed:', error);
    return false;
  }
};

// Fetch CSRF token on module load
api.fetchCsrfToken = fetchCsrfToken;

// Helper to check if user is admin (based on JWT token data)
api.isAdminUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    
    const user = JSON.parse(userStr);
    return user?.is_staff || user?.is_superuser || user?.isAdmin || false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Helper to get current user from localStorage
api.getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Test JWT authentication
api.testAuth = async () => {
  try {
    const response = await api.get('/auth/user/');
    console.log('Auth Test Successful:', response.status);
    return true;
  } catch (error) {
    console.error('Auth Test Failed:', error);
    return false;
  }
};

if (typeof window !== 'undefined') {
  window.api = api;
}

export default api;